import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Circle, Clock3, GripVertical, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type { CustomBoardCard, CustomBoardCardInput, CustomBoardStatus, CustomBoardTag, CustomProduct } from '../../../types/custom';
import { getApiErrorDetails } from '../../../utils/apiError';
import { customErrorMessage } from '../../../utils/customUi';

const COLUMNS = [
  { status: 'backlog', label: 'Backlog', icon: Circle, color: 'text-gray-400' },
  { status: 'in_progress', label: 'In Progress', icon: Clock3, color: 'text-cyber-400' },
  { status: 'done', label: 'Done', icon: Check, color: 'text-neon-400' },
] as const;
const TAGS = [
  { value: 'bug', label: 'Bug', style: 'border-red-500/30 bg-red-500/10 text-red-300' },
  { value: 'feature', label: 'Feature', style: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300' },
] as const;
const DRAG_TYPE = 'application/x-hostinator-board-card';
const MAX_CARDS = 500;
type Editor = { card?: CustomBoardCard; status: CustomBoardStatus };

export function CustomProductBoardPanel({ product }: { product: CustomProduct }) {
  const queryClient = useQueryClient();
  const queryKey = ['custom-products', product.id, 'board'];
  const mounted = useRef(false);
  const mutationPending = useRef(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [error, setError] = useState('');
  const [editorError, setEditorError] = useState('');
  const [notice, setNotice] = useState('');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropColumn, setDropColumn] = useState<CustomBoardStatus | null>(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const board = useQuery({
    queryKey,
    queryFn: async () => (await customAdminApi.listBoardCards(product.id)).cards,
    refetchInterval: 15_000,
  });
  const cards = board.data || [];
  const disabled = Boolean(busy) || !board.data;
  const full = cards.length >= MAX_CARDS;
  const currentEditedCard = editor?.card ? cards.find(card => card.id === editor.card!.id) : undefined;
  const editorStale = Boolean(editor?.card && board.data && currentEditedCard?.version !== editor.card.version);

  const openEditor = (next: Editor) => {
    setEditorError('');
    setError('');
    setNotice('');
    setEditor(next);
  };
  const closeEditor = () => { if (!mutationPending.current) setEditor(null); };

  // Keep the product-specific cache up to date even if navigation unmounts this panel.
  // Only mounted UI receives draft/error changes from an asynchronous response.
  const mutate = async (
    key: string,
    operation: () => Promise<CustomBoardCard | void>,
    success: string,
    inEditor = false,
  ) => {
    if (mutationPending.current) return;
    mutationPending.current = true;
    setBusy(key);
    setError('');
    setEditorError('');
    setNotice('');
    try {
      await queryClient.cancelQueries({ queryKey });
      const saved = await operation();
      queryClient.setQueryData<CustomBoardCard[]>(queryKey, current => {
        if (!current) return current;
        if (!saved) return current.filter(card => card.id !== key);
        return current.some(card => card.id === saved.id)
          ? current.map(card => card.id === saved.id ? saved : card)
          : [...current, saved];
      });
      if (mounted.current) {
        setNotice(success);
        if (inEditor) setEditor(null);
      }
    } catch (caught) {
      const details = getApiErrorDetails(caught, 'The card could not be saved.');
      const message = details.code === 'custom_product_board_card_limit_reached'
        ? `This board has reached its limit of ${MAX_CARDS} cards. Delete a card to add another.`
        : details.status === 409
        ? 'This card changed elsewhere. Your draft has been kept. Load the latest card below before editing it again.'
        : (!details.status || details.status >= 500)
          ? 'The result could not be confirmed. Refresh the board to check whether the change was saved before trying again. Your draft has been kept.'
          : customErrorMessage(caught, 'The card could not be saved.');
      if (mounted.current) (inEditor ? setEditorError : setError)(message);
    } finally {
      await queryClient.invalidateQueries({ queryKey });
      mutationPending.current = false;
      if (mounted.current) setBusy(null);
    }
  };

  const move = (card: CustomBoardCard, status: CustomBoardStatus) => {
    if (card.status === status || mutationPending.current) return;
    void mutate(card.id, async () => (await customAdminApi.updateBoardCard(product.id, card.id, { status, version: card.version })).card,
      `Card moved to ${COLUMNS.find(column => column.status === status)!.label}.`);
  };

  return <section aria-label={`${product.displayName} board`} className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h3 className="text-base font-semibold text-gray-100">Board</h3><p className="mt-1 text-xs text-gray-400">Drag cards between columns, or choose a status on a card.</p></div>
      <div className="flex gap-2">
        <button type="button" className="btn-secondary" disabled={Boolean(busy) || board.isFetching} onClick={() => void board.refetch()} aria-label="Refresh board"><RefreshCw className={`h-4 w-4 ${board.isFetching ? 'animate-spin' : ''}`} />Refresh</button>
        <button type="button" className="btn-primary" disabled={disabled || full} onClick={() => openEditor({ status: 'backlog' })}><Plus className="h-4 w-4" />New card</button>
      </div>
    </div>
    {(error || board.isError) && <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error || customErrorMessage(board.error, 'The board could not be loaded.')} {board.data && 'The last loaded cards are shown.'}</div>}
    <div role="status" aria-live="polite" className="sr-only">{notice || (busy ? 'Saving card…' : '')}</div>
    {full && <p className="text-xs text-amber-300">This board has reached its limit of {MAX_CARDS} cards. Delete a card to add another.</p>}
    {board.isLoading && <div role="status" className="card flex items-center justify-center gap-3 p-12 text-sm text-gray-400"><div className="spinner h-6 w-6" />Loading board…</div>}
    {board.data && <div className="grid gap-4 md:grid-cols-3">
      {COLUMNS.map(column => {
        const columnCards = cards.filter(card => card.status === column.status);
        if (column.status === 'backlog') {
          columnCards.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt) || b.id.localeCompare(a.id));
        }
        const Icon = column.icon;
        return <section key={column.status} aria-label={column.label}
          className={`min-w-0 rounded-xl border p-3 transition-colors ${dropColumn === column.status ? 'border-neon-500 bg-neon-500/10' : 'border-dark-600 bg-dark-800/40'}`}
          onDragOver={event => {
            if (disabled || !draggedId || !event.dataTransfer.types.includes(DRAG_TYPE)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            setDropColumn(column.status);
          }}
          onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropColumn(null); }}
          onDrop={event => {
            event.preventDefault();
            const id = event.dataTransfer.getData(DRAG_TYPE);
            const card = cards.find(item => item.id === id && item.id === draggedId);
            setDraggedId(null);
            setDropColumn(null);
            if (card && !disabled) move(card, column.status);
          }}>
          <div className="mb-3 flex items-center gap-2 px-1">
            <Icon className={`h-4 w-4 ${column.color}`} aria-hidden="true" />
            <h4 className="text-sm font-semibold text-gray-200">{column.label}</h4>
            <span className="rounded-full bg-dark-600 px-2 py-0.5 text-xs text-gray-400" aria-label={`${columnCards.length} cards`}>{columnCards.length}</span>
            <button type="button" className="btn-ghost ml-auto p-1.5" disabled={disabled || full} aria-label={`Add card to ${column.label}`} onClick={() => openEditor({ status: column.status })}><Plus className="h-4 w-4" /></button>
          </div>
          <div className="min-h-32 space-y-3 md:min-h-64">
            {columnCards.length === 0 && <div className={`flex min-h-32 items-center justify-center rounded-lg border border-dashed p-5 text-center text-xs md:min-h-48 ${dropColumn === column.status ? 'border-neon-500/50 text-neon-300' : 'border-dark-600 text-gray-500'}`}>{draggedId ? 'Drop card here' : 'No cards yet'}</div>}
            {columnCards.map(card => <article key={card.id} data-board-card-id={card.id}
              draggable={!disabled}
              onDragStart={event => {
                const target = event.target as HTMLElement;
                if (disabled || target.closest('select')) { event.preventDefault(); return; }
                event.dataTransfer.setData(DRAG_TYPE, card.id);
                event.dataTransfer.effectAllowed = 'move';
                setDraggedId(card.id);
              }}
              onDragEnd={() => { setDraggedId(null); setDropColumn(null); }}
              className={`rounded-lg border border-dark-600 bg-dark-800 p-3 shadow-sm transition-opacity ${draggedId === card.id || busy === card.id ? 'opacity-50' : ''}`}>
              <div className="flex items-start gap-2">
                <GripVertical className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-gray-600" aria-hidden="true" />
                <button type="button" className="min-w-0 flex-1 text-left text-sm font-medium leading-5 text-gray-100 hover:text-neon-300 focus-visible:outline-neon-500 [overflow-wrap:anywhere]" disabled={disabled} onClick={() => openEditor({ card, status: card.status })} aria-label={`Edit card: ${card.title}`}>{card.title}</button>
              </div>
              {card.description && <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-xs leading-5 text-gray-400 [overflow-wrap:anywhere]">{card.description}</p>}
              {(card.tags || []).length > 0 && <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Card tags">{TAGS.filter(tag => (card.tags || []).includes(tag.value)).map(tag => <span key={tag.value} className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${tag.style}`}>{tag.label}</span>)}</div>}
              <label className="mt-3 block"><span className="sr-only">Status for {card.title}</span><select className="input w-full py-1.5 text-xs" value={card.status} disabled={disabled} onChange={event => move(card, event.target.value as CustomBoardStatus)}>{COLUMNS.map(option => <option key={option.status} value={option.status}>{option.label}</option>)}</select></label>
            </article>)}
          </div>
        </section>;
      })}
    </div>}
    {editor && <CardEditor key={editor.card ? `${editor.card.id}:${editor.card.version}` : 'new'} editor={editor} busy={Boolean(busy)} error={editorError} stale={editorStale}
      onLoadLatest={editorStale && currentEditedCard ? () => { setEditorError(''); setEditor({ card: currentEditedCard, status: currentEditedCard.status }); } : undefined}
      onClose={closeEditor}
      onSave={input => void mutate(editor.card?.id || 'create', async () => editor.card
        ? (await customAdminApi.updateBoardCard(product.id, editor.card.id, { ...input, version: editor.card.version })).card
        : (await customAdminApi.createBoardCard(product.id, input)).card, editor.card ? 'Card updated.' : 'Card created.', true)}
      onDelete={editor.card ? () => void mutate(editor.card!.id, async () => { await customAdminApi.deleteBoardCard(product.id, editor.card!.id, editor.card!.version); }, 'Card deleted.', true) : undefined} />}
  </section>;
}

function CardEditor({ editor, busy, error, stale, onLoadLatest, onClose, onSave, onDelete }: {
  editor: Editor;
  busy: boolean;
  error: string;
  stale: boolean;
  onLoadLatest?: () => void;
  onClose: () => void;
  onSave: (input: CustomBoardCardInput) => void;
  onDelete?: () => void;
}) {
  const [title, setTitle] = useState(editor.card?.title || '');
  const [description, setDescription] = useState(editor.card?.description || '');
  const [status, setStatus] = useState(editor.status);
  const [tags, setTags] = useState<CustomBoardTag[]>(editor.card?.tags || []);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const titleInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    element?.showModal();
    titleInput.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      element?.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(<dialog ref={dialog} aria-labelledby="board-card-editor-title"
    className="m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-xl border border-dark-600 bg-dark-800 p-0 text-gray-200 shadow-2xl backdrop:bg-black/70 backdrop:backdrop-blur-sm"
    onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <form onSubmit={event => { event.preventDefault(); if (!busy && !stale && title.trim()) onSave({ title: title.trim(), description, status, tags }); }}>
      <div className="modal-header"><h2 id="board-card-editor-title" className="modal-title">{editor.card ? 'Edit card' : 'New card'}</h2><button type="button" className="btn-ghost p-2" disabled={busy} onClick={onClose} aria-label="Close card editor"><X className="h-4 w-4" /></button></div>
      <div className="modal-body space-y-4">
        {error && <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
        {stale && <div role="status" className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">{onLoadLatest ? <><p>This card has a newer version. Your draft is still shown.</p><button type="button" className="btn-secondary mt-3" disabled={busy} onClick={onLoadLatest}>Replace draft with latest card</button></> : <p>This card has been deleted. Your draft is still shown for reference.</p>}</div>}
        <label className="block text-sm text-gray-400">Title<input ref={titleInput} className="input mt-1" required maxLength={160} disabled={busy} value={title} onChange={event => setTitle(event.target.value)} placeholder="What needs to be done?" /></label>
        <label className="block text-sm text-gray-400">Description <span className="text-xs text-gray-500">(optional)</span><textarea className="input mt-1 min-h-32 resize-y" maxLength={5000} disabled={busy} value={description} onChange={event => setDescription(event.target.value)} placeholder="Add a few details…" /></label>
        <label className="block text-sm text-gray-400">Status<select className="input mt-1" value={status} disabled={busy} onChange={event => setStatus(event.target.value as CustomBoardStatus)}>{COLUMNS.map(column => <option key={column.status} value={column.status}>{column.label}</option>)}</select></label>
        <fieldset disabled={busy}>
          <legend className="text-sm text-gray-400">Tags <span className="text-xs text-gray-500">(optional)</span></legend>
          <div className="mt-2 flex flex-wrap gap-2">{TAGS.map(tag => <label key={tag.value} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tags.includes(tag.value) ? tag.style : 'border-dark-600 text-gray-400'} ${busy ? 'cursor-default opacity-50' : ''}`}><input type="checkbox" className="h-4 w-4 accent-neon-500" checked={tags.includes(tag.value)} onChange={event => { const checked = event.target.checked; setTags(current => TAGS.filter(option => option.value === tag.value ? checked : current.includes(option.value)).map(option => option.value)); }} />{tag.label}</label>)}</div>
        </fieldset>
        {confirmDelete && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200"><p>Delete this card? This cannot be undone.</p><div className="mt-3 flex flex-wrap gap-2"><button type="button" className="btn-danger" disabled={busy || stale} onClick={onDelete}>Delete card</button><button type="button" className="btn-secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep card</button></div></div>}
      </div>
      <div className="modal-footer flex-wrap">
        {onDelete && !confirmDelete && <button type="button" className="btn-ghost mr-auto text-red-400" disabled={busy} onClick={() => setConfirmDelete(true)}><Trash2 className="h-4 w-4" />Delete</button>}
        <button type="button" className="btn-secondary" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={busy || stale || !title.trim() || confirmDelete}>{busy ? 'Saving…' : editor.card ? 'Save changes' : 'Create card'}</button>
      </div>
    </form>
  </dialog>, document.body);
}
